import { useRef, useState } from "react";
import { Plus, Trash2, UploadCloud, Image as ImageIcon, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbsolute = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

// Downscale + re-encode client-side to keep DB payload small.
const resizeImage = (file, maxW = 1600, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Invalid image"));
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        const type = file.type === "image/png" ? "image/png" : (file.type === "image/webp" ? "image/webp" : "image/jpeg");
        resolve(canvas.toDataURL(type, quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

export const PhotosEditor = ({ images, primary, onImages, onPrimary, documents, onDocuments }) => {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [url, setUrl] = useState("");
  const [docName, setDocName] = useState(""); const [docUrl, setDocUrl] = useState("");
  const [drag, setDrag] = useState(false);

  const addImgUrl = () => { if (url) { onImages([...images, url]); setUrl(""); } };
  const removeImg = (i) => { const n = images.filter((_, idx) => idx !== i); onImages(n); if (primary >= n.length) onPrimary(0); };
  const move = (i, dir) => {
    const n = [...images]; const t = i + dir; if (t < 0 || t >= n.length) return;
    [n[i], n[t]] = [n[t], n[i]]; onImages(n);
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /^image\/(jpeg|png|webp)$/.test(f.type));
    if (!files.length) { toast.error("Only JPG, PNG or WEBP images"); return; }
    setUploading(true); setProgress({ done: 0, total: files.length });
    const uploaded = [];
    for (const [i, file] of files.entries()) {
      try {
        const data_url = await resizeImage(file);
        const { data } = await api.post("/upload", { data_url, label: file.name });
        uploaded.push(data.url);
        setProgress({ done: i + 1, total: files.length });
      } catch (e) {
        toast.error(`${file.name}: ${formatApiError(e)}`);
      }
    }
    if (uploaded.length) {
      onImages([...images, ...uploaded]);
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} uploaded`);
    }
    setUploading(false); setProgress({ done: 0, total: 0 });
    if (fileInput.current) fileInput.current.value = "";
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); };

  const addDoc = () => { if (docUrl) { onDocuments([...documents, { name: docName || "Document", url: docUrl, type: "document" }]); setDocName(""); setDocUrl(""); } };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-display font-bold text-sm text-gray-900 mb-2 flex items-center gap-2"><ImageIcon size={14} /> Vehicle Photos</h3>

        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${drag ? "border-[#940C2C] bg-[#F6E6EB]" : "border-gray-300 hover:border-gray-900 bg-gray-50"}`}
          data-testid="photo-upload-dropzone"
        >
          <input ref={fileInput} type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={(e) => handleFiles(e.target.files)} className="hidden" data-testid="photo-upload-input" />
          <div className="flex flex-col items-center gap-2">
            {uploading
              ? <><Loader2 size={28} className="text-[#940C2C] animate-spin" /><div className="text-sm font-semibold text-gray-900">Uploading {progress.done}/{progress.total}…</div></>
              : <><UploadCloud size={28} className="text-[#940C2C]" />
                  <div className="text-sm font-semibold text-gray-900">Drop photos here or click to browse</div>
                  <div className="text-xs text-gray-500">JPG, PNG or WEBP · multiple files · auto-resized to 1600px wide</div>
                </>}
          </div>
        </label>

        <div className="mt-3 flex gap-2 items-center text-xs text-gray-500">
          <span>Or paste an image URL:</span>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-xs" data-testid="img-url-input" />
          <button type="button" onClick={addImgUrl} className="btn-dark !py-1.5 !px-3 text-xs" data-testid="img-add-btn"><Plus size={12} /> Add</button>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {images.map((im, i) => (
            <div key={i} className={`relative rounded-lg overflow-hidden border-2 ${primary === i ? "border-[#940C2C]" : "border-gray-200"}`}>
              <img src={toAbsolute(im)} alt="" className="w-full aspect-[4/3] object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] p-1.5 flex items-center justify-between gap-1">
                <button onClick={() => onPrimary(i)} className={`font-bold ${primary === i ? "text-[#F6E6EB]" : "hover:text-white"}`}>{primary === i ? "COVER" : "Set cover"}</button>
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} title="Move left">←</button>
                  <button onClick={() => move(i, 1)} title="Move right">→</button>
                  <button onClick={() => removeImg(i)} className="text-rose-300" title="Remove"><Trash2 size={10} /></button>
                </div>
              </div>
            </div>
          ))}
          {images.length === 0 && !uploading && <div className="col-span-full border border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-400 text-sm">No photos yet.</div>}
        </div>
      </div>

      <div className="pt-6 border-t">
        <h3 className="font-display font-bold text-sm text-gray-900 mb-2">Private Documents (admin only)</h3>
        <p className="text-[11px] text-gray-400 mb-2">URLs for RC, insurance, PUC, service history, inspection report. Not shown publicly.</p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
          <input value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="Document name" className="rounded-md border border-gray-200 px-3 py-2 text-sm" />
          <input value={docUrl} onChange={(e) => setDocUrl(e.target.value)} placeholder="Document URL" className="rounded-md border border-gray-200 px-3 py-2 text-sm" />
          <button type="button" onClick={addDoc} className="btn-dark !py-2"><Plus size={14} /> Add</button>
        </div>
        <div className="mt-3 space-y-1">
          {documents.map((d, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 rounded px-3 py-2 text-sm">
              <ExternalLink size={13} className="text-gray-500" />
              <a href={d.url} target="_blank" rel="noreferrer" className="flex-1 truncate text-gray-900">{d.name}</a>
              <button onClick={() => onDocuments(documents.filter((_, idx) => idx !== i))}><Trash2 size={13} className="text-red-500" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
